import React, { memo } from 'react';
import OperatorSection from './OperatorSection';

const TrainingSection = memo(({
  formData,
  setFormData,
  isEditing = true,
  metadataFields,
  distinctValues
}) => {
    return (
    <div className="bg-white rounded-lg shadow p-4 lg:p-6">
      <h2 className="text-lg font-semibold mb-4">Training</h2>

      {/* Attribute */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Target attribute for sampling</label>
        <div className="relative">
          <select
            className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={formData.attribute}
            onChange={(e) => setFormData({
              ...formData,
              attribute: e.target.value,
              // Reset multi-class classes when attribute changes
              modeling_type: {
                ...formData.modeling_type,
                classes: [],
                case: [],
                control: []
              }
            })}
            disabled={!isEditing}
          >
            {metadataFields.map(option => (
              <option key={option.name} value={option.name}>{option.label}</option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Filter Data Training */}
      <OperatorSection
        title="Filter Data Training"
        description="Filter system for training data"
        filters={formData.filter_data_training}
        sectionKey="filter_data_training"
        formData={formData}
        setFormData={setFormData}
        isEditing={isEditing}
        metadataFields={metadataFields}
        distinctValues={distinctValues}
      />

      {/* Features to Include */}
      <div className="mb-6">
        <div className="text-sm text-gray-600 mb-3">Feature selection for training</div>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-3 rounded focus:ring-2 focus:ring-blue-500"
              checked={formData.features_to_include.include_vocs}
              onChange={(e) => setFormData({
                ...formData,
                features_to_include: {
                  ...formData.features_to_include,
                  include_vocs: e.target.checked
                }
              })}
              disabled={!isEditing}
            />
            <span>Voc</span>
            <span className="text-sm text-gray-500 ml-2">Description</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-3 rounded focus:ring-2 focus:ring-blue-500"
              checked={formData.features_to_include.include_demo}
              onChange={(e) => setFormData({
                ...formData,
                features_to_include: {
                  ...formData.features_to_include,
                  include_demo: e.target.checked
                }
              })}
              disabled={!isEditing}
            />
            <span>Demo</span>
            <span className="text-sm text-gray-500 ml-2">Description</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              className="mr-3 rounded focus:ring-2 focus:ring-blue-500"
              checked={formData.features_to_include.include_mirex}
              onChange={(e) => setFormData({
                ...formData,
                features_to_include: {
                  ...formData.features_to_include,
                  include_mirex: e.target.checked
                }
              })}
              disabled={!isEditing}
            />
            <span>Mirex</span>
            <span className="text-sm text-gray-500 ml-2">Description</span>
          </label>
        </div>
      </div>

      {/* Validation Techniques */}
      <div>
        <div className="text-sm text-gray-600 mb-3">Validation technique configuration</div>
        <div className="space-y-3">
          <label className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <div className="flex items-center">
              <input
                type="radio"
                name="validation_technique"
                className="mr-3 focus:ring-2 focus:ring-blue-500"
                checked={formData.validation_technique?.name === 'k-fold'}
                onChange={() => setFormData({
                  ...formData,
                  validation_technique: { ...(formData.validation_technique || {}), name: 'k-fold' }
                })}
                disabled={!isEditing}
              />
              <span>K fold</span>
            </div>
            <input
              type="number"
              className="w-full sm:w-20 border rounded px-2 py-1 sm:ml-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="K value"
              value={formData.validation_technique?.k || 5}
              onChange={(e) => setFormData({
                ...formData,
                validation_technique: { ...(formData.validation_technique || {}), k: parseInt(e.target.value) || 5 }
              })}
              disabled={!isEditing}
            />
          </label>
          <label className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <div className="flex items-center">
              <input
                type="radio"
                name="validation_technique"
                className="mr-3 focus:ring-2 focus:ring-blue-500"
                checked={formData.validation_technique?.name === 'train-test-split'}
                onChange={() => setFormData({
                  ...formData,
                  validation_technique: { ...(formData.validation_technique || {}), name: 'train-test-split' }
                })}
                disabled={!isEditing}
              />
              <span>Train Test Split</span>
            </div>
            <input
              type="text"
              className="w-full sm:w-20 border rounded px-2 py-1 sm:ml-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="20%"
              value={(formData.validation_technique?.test_size || 0.2) * 100 + '%'}
              onChange={(e) => {
                // Remove % symbol and parse as float
                const numericValue = parseFloat(e.target.value.replace('%', ''));
                // Convert percentage to decimal (e.g., 20% -> 0.2)
                const decimalValue = isNaN(numericValue) ? 0.2 : numericValue / 100;
                // Clamp between 0 and 1
                const clampedValue = Math.max(0, Math.min(1, decimalValue));
                setFormData({
                  ...formData,
                  validation_technique: { ...(formData.validation_technique || {}), test_size: clampedValue }
                });
              }}
              disabled={!isEditing}
            />
          </label>
        </div>
      </div>
    </div>
  );
});

TrainingSection.displayName = 'TrainingSection';

export default TrainingSection;
